import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CustomerService from "../../../../rest/customerService";
import ParentEditForm from "../../forms/parentEditForm";
import EditableStudentsList from "./parentStudentListEdit";
import StudentSearch from "./parentStudentSearch";
import CustomButton from "../../../../components/lib/customButton";
import Spinner from "../../../../components/lib/spinner";
import Stepper from "../../../../components/lib/stepperHeader";

const STEP = {
    PARENT_DATA:  0,
    PARENTING: 1,
    SUMMARY: 2,
};

const steps = [
    { key: STEP.PARENT_DATA, label: "Responsável" },
    { key: STEP.PARENTING, label: "Parentesco" },
    { key: STEP.SUMMARY, label: "Resumo" },
];

const ParentEditStepper = ({
    parent,
    students,
    onFinish,
    onCancel,
    onDirty,
}) => {
    const [step, setStep] = useState(STEP.PARENT_DATA);

    const [parentDraft, setParentDraft] = useState(null);

    const [linkedStudents, setLinkedStudents] = useState([]);
    const [initialStudentIds, setInitialStudentIds] = useState([]);

    const [studentsToAdd, setStudentsToAdd] = useState([]);
    const [studentsToRemove, setStudentsToRemove] = useState([]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!parent) return;

        setStep(STEP.PARENT_DATA);
        setParentDraft(parent);

        setLinkedStudents(students || []);
        setInitialStudentIds((students || []).map(s => s.id_student));

        setStudentsToAdd([]);
        setStudentsToRemove([]);
        setIsSubmitting(false);
    }, [parent, students]);


    const submitActiveForm = useCallback(() => {
        if (isSubmitting) return;

        document
            .querySelector("form[data-step-form]")
            ?.requestSubmit();
    }, [isSubmitting]);


    const goBack = () => {
        if (isSubmitting) return;

        if (step === STEP.PARENT_DATA) {
            onCancel?.();
            return;
        }

        setStep(s => s - 1);
    };


    const handleAddStudent = (student) => {
        if (linkedStudents.some(s => s.id_student === student.id_student)) return;

        setLinkedStudents(prev => [...prev, student]);

        if (!initialStudentIds.includes(student.id_student)) {
            setStudentsToAdd(prev => [...prev, student.id_student]);
        }

        setStudentsToRemove(prev =>
            prev.filter(id => id !== student.id_student)
        );

        onDirty?.();
    };

    const handleRemoveStudent = (student) => {
        setLinkedStudents(prev =>
            prev.filter(s => s.id_student !== student.id_student)
        );

        if (initialStudentIds.includes(student.id_student)) {
            setStudentsToRemove(prev => [...prev, student.id_student]);
        }

        setStudentsToAdd(prev =>
            prev.filter(id => id !== student.id_student)
        );

        onDirty?.();
    };


    const handleFinalSubmit = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);

        try {
            let chain = Promise.resolve();

            if (JSON.stringify(parentDraft) !== JSON.stringify(parent)) {
                chain = chain.then(() =>
                    CustomerService.updateParent(parent.login, parentDraft)
                );
            }

            if (studentsToRemove.length > 0) {
                chain = chain.then(() =>
                    Promise.all(
                        studentsToRemove.map(studentId =>
                            CustomerService.removeStudentsFromParentRecord(
                                parent.login,
                                studentId
                            )
                        )
                    )
                );
            }

            if (studentsToAdd.length > 0) {
                chain = chain.then(() =>
                    Promise.all(
                        studentsToAdd.map(studentId =>
                            CustomerService.addParenting(
                                parent.id_parent,
                                studentId
                            )
                        )
                    )
                );
            }

            await chain;

            onFinish?.({
                parent: parentDraft,
                students: linkedStudents,
            });

        } catch (err) {
            console.error(err);
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <Stepper
                steps={steps}
                currentStep={step}
            />

            {/* CONTENT */}
            <div className="min-h-[420px]">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                    >
                        {step === STEP.PARENT_DATA && parentDraft && (
                            <ParentEditForm
                                initialData={parentDraft}
                                onDirty={onDirty}
                                onSubmit={(data) => {
                                    setParentDraft(data);
                                    setStep(STEP.PARENTING);
                                }}
                            />
                        )}

                        {step === STEP.PARENTING && (
                            <>
                                <EditableStudentsList
                                    students={linkedStudents}
                                    onRemove={handleRemoveStudent}
                                />

                                <StudentSearch
                                    excludedIds={linkedStudents.map(s => s.id_student)}
                                    onSelectStudent={handleAddStudent}
                                />
                            </>
                        )}

                        {step === STEP.SUMMARY && parentDraft && (
                            <div className="space-y-5 text-sm">

                                <div className="space-y-1">
                                    <p className="font-semibold text-base">Responsável</p>

                                    <p>
                                        <span className="font-medium">Nome:</span>{" "}
                                        {parentDraft.name}
                                    </p>

                                    {parentDraft.email !== parent.email && (
                                        <p className="text-blue-600">
                                            Email alterado
                                        </p>
                                    )}

                                    {parentDraft.phone_number !== parent.phone_number && (
                                        <p className="text-blue-600">
                                            Telefone alterado
                                        </p>
                                    )}
                                </div>
                                <hr />

                                <div className="space-y-1">
                                    <p className="font-semibold text-base">
                                        Alunos vinculados ({linkedStudents.length})
                                    </p>

                                    {linkedStudents.length === 0 && (
                                        <p className="text-gray-500 italic">
                                            Nenhum aluno vinculado
                                        </p>
                                    )}

                                    {linkedStudents.map(student => (
                                        <div
                                            key={student.id_student}
                                            className="flex justify-between text-xs border-b py-1"
                                        >
                                            <span>{student.name}</span>
                                            <span className="text-gray-500">
                                                {student.grade}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {studentsToAdd.length > 0 && (
                                    <>
                                        <hr />
                                        <div className="space-y-1">
                                            <p className="font-semibold text-green-600">
                                                + Vínculos adicionados ({studentsToAdd.length})
                                            </p>

                                            {studentsToAdd.map(id => {
                                                const student = linkedStudents.find(
                                                    s => s.id_student === id
                                                );

                                                return (
                                                    <p key={id} className="text-xs">
                                                        {student?.name} — {student?.grade}
                                                    </p>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}

                                {studentsToRemove.length > 0 && (
                                    <>
                                        <hr />
                                        <div className="space-y-1">
                                            <p className="font-semibold text-red-600">
                                                − Vínculos removidos ({studentsToRemove.length})
                                            </p>

                                            {studentsToRemove.map(id => {
                                                const student = students.find(
                                                    s => s.id_student === id
                                                );

                                                return (
                                                    <p key={id} className="text-xs">
                                                        {student?.name} — {student?.grade}
                                                    </p>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}

                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* FOOTER */}
            <div className="flex justify-between">
                <CustomButton
                    buttonType={step === STEP.PARENT_DATA ? "cancel" : "confirmLater"}
                    text={step === STEP.PARENT_DATA ? "Cancelar" : "Voltar"}
                    showIcon={false}
                    disabled={isSubmitting}
                    onClick={goBack}
                />

                {step < STEP.SUMMARY && (
                    <CustomButton
                        buttonType="confirm"
                        text="Avançar"
                        showIcon={false}
                        disabled={isSubmitting}
                        onClick={
                            step === STEP.PARENT_DATA
                                ? submitActiveForm
                                : () => setStep(s => s + 1)
                        }
                    />
                )}

                {step === STEP.SUMMARY && (
                    <CustomButton
                        buttonType="confirm"
                        showIcon={false}
                        disabled={isSubmitting}
                        onClick={handleFinalSubmit}
                        text={
                            isSubmitting ? (
                                <>
                                    <span className="ml-2">Processando</span>
                                    <Spinner />
                                </>
                            ) : (
                                "Salvar alterações"
                            )}
                    />
                )}
            </div>
        </div>
    );
};

export default ParentEditStepper;
